/**
 * Trade Feed Component
 * Real-time stream of large trades
 */

import { useMemo } from 'react';
import { ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAlertStore, type Trade, type Asset } from '@/stores/alert-store';
import { formatRelativeTime, formatUSD, formatPrice, cn } from '@/lib/utils';

interface TradeFeedProps {
  asset?: Asset;
  limit?: number;
}

export function TradeFeed({ asset, limit = 20 }: TradeFeedProps) {
  const { trades } = useAlertStore();

  const filteredTrades = useMemo(() => {
    let result = trades;
    if (asset) {
      result = result.filter((t) => t.asset === asset);
    }
    return result.slice(0, limit);
  }, [trades, asset, limit]);

  if (filteredTrades.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            Large Trades
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex h-40 items-center justify-center text-muted-foreground">
            No large trades yet
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Large Trades
          {asset && <Badge variant="outline">{asset}</Badge>}
        </CardTitle>
      </CardHeader>
      <CardContent className="max-h-[600px] overflow-y-auto">
        <div className="space-y-2">
          {filteredTrades.map((trade) => (
            <TradeItem key={trade.id} trade={trade} />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

interface TradeItemProps {
  trade: Trade;
}

function TradeItem({ trade }: TradeItemProps) {
  const isBuy = trade.side === 'buy';

  return (
    <div
      className={cn(
        'flex items-center gap-3 rounded-lg border p-3 transition-colors',
        isBuy && 'border-green-500/20 bg-green-500/5',
        !isBuy && 'border-red-500/20 bg-red-500/5'
      )}
    >
      {/* Direction Icon */}
      <div className={cn('text-lg', isBuy ? 'text-green-500' : 'text-red-500')}>
        {isBuy ? (
          <ArrowUpRight className="h-5 w-5" />
        ) : (
          <ArrowDownRight className="h-5 w-5" />
        )}
      </div>

      {/* Asset & Exchange */}
      <div className="flex items-center gap-2">
        <Badge
          variant={isBuy ? 'bullish' : 'bearish'}
          className="text-xs"
        >
          {trade.asset}
        </Badge>
        <span className="text-xs text-muted-foreground">
          {trade.exchange}
        </span>
      </div>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Trade Details */}
      <div className="text-right">
        <div className={cn('font-bold tabular-nums', isBuy ? 'text-green-500' : 'text-red-500')}>
          {formatUSD(trade.usd)}
        </div>
        <div className="text-xs text-muted-foreground tabular-nums">
          {trade.size.toFixed(4)} @ {formatPrice(trade.price)}
        </div>
      </div>

      {/* Timestamp */}
      <div className="text-xs text-muted-foreground w-16 text-right">
        {formatRelativeTime(trade.timestamp)}
      </div>
    </div>
  );
}
